import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Who has not read what. SC-015, task T-doc-report.
 *
 * Fetched on demand, like the job cost panel and for the same two reasons: most
 * people opening the procedures never need it, and it is the request a staff
 * account is refused, so loading it with the page would put a 403 in the
 * console of everybody who works here.
 *
 * It reports the refusal in words rather than rendering an empty table. **An
 * empty table here reads as "everybody is up to date"**, which is the opposite
 * of what a refusal means and the more dangerous of the two to get wrong.
 */
interface OutstandingRow {
  document_id: string;
  title: string;
  category: string;
  user_id: string;
  full_name: string | null;
}

export function ReadingRecord() {
  const [open, setOpen] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ['documents', 'outstanding'],
    enabled: open,
    retry: false,
    queryFn: async (): Promise<{ rows: OutstandingRow[] }> => {
      const res = await fetch('/api/documents-outstanding', { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return (await res.json()) as { rows: OutstandingRow[] };
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
      >
        Who has not read what
      </button>
    );
  }

  const byPerson = new Map<string, OutstandingRow[]>();
  for (const r of data?.rows ?? []) {
    const key = r.full_name ?? r.user_id;
    byPerson.set(key, [...(byPerson.get(key) ?? []), r]);
  }

  return (
    <section className="mb-8 rounded-[10px] border border-line bg-panel p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-[15px] font-bold">Who has not read what</h2>
        <button type="button" onClick={() => setOpen(false)} className="text-[14px] text-muted">
          Close
        </button>
      </div>

      {isLoading && <p className="text-[14px] text-muted">Working it out…</p>}

      {error && (
        <p className="text-[14px] text-muted">{(error as Error).message}</p>
      )}

      {data && data.rows.length === 0 && (
        <p className="text-[14px] text-muted">
          Everybody who works here has read the current version of everything
          published. This is the real answer, not an empty result: a refusal
          would have said so instead.
        </p>
      )}

      {data && data.rows.length > 0 && (
        <div className="grid gap-3">
          {[...byPerson.entries()].map(([person, rows]) => (
            <div key={person} className="text-[14px]">
              <span className="font-semibold">{person}</span>
              <span className="text-muted"> · {rows.length} to read</span>
              <ul className="mt-1 list-disc pl-5 text-[13px] text-muted">
                {rows.map((r) => (
                  <li key={`${r.user_id}-${r.document_id}`}>{r.title}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
